import { dollarsToCents, formatUsd, sumCents } from "./money";
import type { Coverage, EstimateExtraction, EstimateItem, RawEstimate, ReconcileCheck, RpsSummary } from "./types";

/**
 * Carrier-estimate reading: converting the AI's output to cents, normalizing it, and the
 * "self-correcting totals check" that ties every line back to the estimate's own printed totals.
 * Ported from the Worker's src/estimate.ts (`normalize`, `lineMode`, `autoAssignCoverage`,
 * `reconcile`, and the retry decision in `extractEstimate`).
 */

const optCents = (n: number | undefined): number | undefined => (n === undefined ? undefined : dollarsToCents(n));

/** Converts the AI's reading (dollars, as validated by `rawEstimateSchema`) to cents. Doesn't normalize. */
export function extractionFromRaw(raw: RawEstimate): EstimateExtraction {
  const rps = (r: NonNullable<RawEstimate["coverages"][number]["rps"]>): RpsSummary => ({
    lineItemTotalCents: optCents(r.lineItemTotal),
    lineItemRpsTotalCents: optCents(r.lineItemRpsTotal),
    taxCents: optCents(r.tax),
    overheadCents: optCents(r.overhead),
    profitCents: optCents(r.profit),
    rcvCents: optCents(r.rcv),
    customerPortionCents: optCents(r.customerPortion),
    rpsValueCents: optCents(r.rpsValue),
  });
  const lit = raw.lineItemTotals;
  return {
    claim: {
      carrier: raw.claim.carrier,
      claimNumber: raw.claim.claimNumber,
      policyNumber: raw.claim.policyNumber,
      dateOfLoss: raw.claim.dateOfLoss,
      insured: raw.claim.insured,
      propertyAddress: raw.claim.propertyAddress,
    },
    coverages: raw.coverages.map((cv) => ({
      code: cv.code,
      name: cv.name,
      lineItemTotalCents: dollarsToCents(cv.lineItemTotal),
      taxCents: dollarsToCents(cv.tax),
      overheadCents: dollarsToCents(cv.overhead),
      profitCents: dollarsToCents(cv.profit),
      rcvCents: dollarsToCents(cv.rcv),
      acvCents: dollarsToCents(cv.acv),
      deductibleCents: dollarsToCents(cv.deductible),
      recoverableDepreciationCents: dollarsToCents(cv.recoverableDepreciation),
      nonRecoverableDepreciationCents: dollarsToCents(cv.nonRecoverableDepreciation),
      ...(cv.paidWhenIncurred !== undefined ? { paidWhenIncurred: cv.paidWhenIncurred } : {}),
      ...(cv.linesInclude !== undefined ? { linesInclude: cv.linesInclude } : {}),
      ...(cv.priorPayments !== undefined ? { priorPaymentsCents: dollarsToCents(cv.priorPayments) } : {}),
      ...(cv.rps ? { rps: rps(cv.rps) } : {}),
    })),
    items: raw.items.map((it) => ({
      n: it.n,
      description: it.description ?? "",
      area: it.area ?? "",
      coverage: it.coverage ?? "",
      trade: it.trade ?? "",
      building: it.building ?? "",
      rcvCents: dollarsToCents(it.rcv),
      depreciationCents: dollarsToCents(it.depreciation),
      recoverable: it.recoverable,
      acvCents: dollarsToCents(it.acv),
      ...(it.rpsFactor !== undefined ? { rpsFactor: it.rpsFactor } : {}),
      ...(it.rpsTotal !== undefined ? { rpsTotalCents: dollarsToCents(it.rpsTotal) } : {}),
      ...(it.paidWhenIncurredAmount !== undefined ? { paidWhenIncurredAmountCents: dollarsToCents(it.paidWhenIncurredAmount) } : {}),
    })),
    ...(lit ? { lineItemTotals: { rcvCents: optCents(lit.rcv), depreciationCents: optCents(lit.depreciation), acvCents: optCents(lit.acv) } } : {}),
    ...(raw.groupSummaries ? { groupSummaries: { ...raw.groupSummaries } } : {}),
  };
}

/** Coverage names that mean Ordinance or Law (paid when incurred). */
const PWI_COVERAGE = /ordinance|code upgrade|paid when incurred/i;

/**
 * Worker `normalize()`. Returns a new extraction:
 * - tidies descriptions; empty building → "Main House", empty trade → "General";
 * - a $0 line with a paid-when-incurred amount (State Farm "PWARR") becomes a line for that
 *   amount, fully recoverable, flagged `outsideTotals` (it isn't in the estimate's totals);
 * - drops coverages with no RCV; marks Ordinance or Law coverages paid-when-incurred;
 * - assigns lines to coverages (`autoAssignCoverage`);
 * - when a coverage's depreciation is all non-recoverable (or all recoverable), sets every one of its lines to match.
 */
export function normalizeExtraction(input: EstimateExtraction): EstimateExtraction {
  const ex: EstimateExtraction = structuredClone(input);
  ex.items = (ex.items ?? []).map((it) => {
    const out: EstimateItem = {
      ...it,
      description: String(it.description ?? "").replace(/\s+/g, " ").trim(),
      building: it.building || "Main House",
      trade: it.trade || "General",
    };
    if (!out.rcvCents && (out.paidWhenIncurredAmountCents ?? 0) > 0) {
      out.outsideTotals = true;
      out.rcvCents = out.acvCents = out.paidWhenIncurredAmountCents!;
      out.depreciationCents = 0;
      out.recoverable = true;
    }
    return out;
  });
  ex.coverages = (ex.coverages ?? []).filter((cv) => cv.rcvCents > 0);
  for (const cv of ex.coverages) if (PWI_COVERAGE.test(`${cv.code} ${cv.name}`)) cv.paidWhenIncurred = true;
  autoAssignCoverage(ex);
  for (const cv of ex.coverages) {
    const its = ex.items.filter((i) => i.coverage === cv.code && !i.outsideTotals);
    if (cv.recoverableDepreciationCents === 0 && cv.nonRecoverableDepreciationCents > 0) its.forEach((i) => (i.recoverable = false));
    else if (cv.nonRecoverableDepreciationCents === 0 && cv.recoverableDepreciationCents > 0) its.forEach((i) => (i.recoverable = true));
  }
  return ex;
}

/** Sum of the printed RCV of a coverage's lines (PWARR lines outside the totals left out). */
export function coverageLineSumCents(ex: EstimateExtraction, code: string): number {
  return sumCents(
    ex.items.filter((i) => i.coverage === code && !i.outsideTotals),
    (i) => i.rcvCents,
  );
}

/** How a coverage's line RCVs relate to its summary: whether tax and/or O&P are already in each line. */
export interface LineMode {
  taxIn: boolean;
  opIn: boolean;
  /** The summary figure the lines add up to. */
  targetCents: number;
}

/**
 * Worker `lineMode()`: which of line-item total, + tax, + O&P, or the full RCV the coverage's
 * lines add up to; null when none. When several match, the AI's `linesInclude` wins, else the first.
 */
export function lineMode(ex: EstimateExtraction, cv: Coverage): LineMode | null {
  const s = coverageLineSumCents(ex, cv.code);
  const lit = cv.lineItemTotalCents, tax = cv.taxCents, op = cv.overheadCents + cv.profitCents;
  const cands: [string, number][] = [
    ["nothing", lit],
    ["tax", lit + tax],
    ["op", lit + op],
    ["tax_and_op", cv.rcvCents],
  ];
  const hits = cands.filter(([, v]) => v === s);
  if (!hits.length) return null;
  const pick = hits.find(([k]) => k === cv.linesInclude) ?? hits[0]!;
  return { taxIn: pick[0] === "tax" || pick[0] === "tax_and_op", opIn: pick[0] === "op" || pick[0] === "tax_and_op", targetCents: pick[1] };
}

/**
 * Worker `autoAssignCoverage()` (mutates `ex`). With one coverage, every line goes to it. With
 * several, if some coverage's lines don't add up, it tries every assignment of the "floating"
 * lines (General / labor minimum / debris / dumpster / haul lines, or lines with an unknown
 * coverage; at most 9 of them) until every coverage ties out, else leaves them as read.
 */
export function autoAssignCoverage(ex: EstimateExtraction): void {
  const codes = ex.coverages.map((cv) => cv.code);
  if (codes.length < 2) {
    ex.items.forEach((it) => (it.coverage = codes[0] ?? it.coverage));
    return;
  }
  const ok = () => ex.coverages.every((cv) => lineMode(ex, cv) !== null) && ex.items.every((i) => codes.includes(i.coverage));
  if (ok()) return;
  const floating = ex.items.filter((it) => /general|labor min|debris|dumpster|haul/i.test(`${it.area} ${it.description}`) || !codes.includes(it.coverage));
  if (!floating.length || floating.length > 9) return;
  const total = Math.pow(codes.length, floating.length);
  const original = floating.map((it) => it.coverage);
  for (let k = 0; k < total; k++) {
    let x = k;
    for (const it of floating) {
      it.coverage = codes[x % codes.length]!;
      x = Math.floor(x / codes.length);
    }
    if (ok()) return;
  }
  floating.forEach((it, i) => (it.coverage = original[i]!));
}

/** RPS reduction on a line: RCV − RPS Total (≥ 0); 0 for non-RPS lines and PWARR lines. Worker `rpsCutOf()`. */
export function rpsCutCents(i: EstimateItem): number {
  return i.rpsTotalCents != null && !i.outsideTotals ? Math.max(0, i.rcvCents - i.rpsTotalCents) : 0;
}

/** The deductible check is informational: a $0 deductible doesn't fail the reading. */
export const DEDUCTIBLE_CHECK_NAME = "Deductible found";

/** Worker `reconcile()`: checks an (already normalized) extraction against the estimate's own printed totals. */
export function reconcileExtraction(ex: EstimateExtraction): ReconcileCheck[] {
  const checks: ReconcileCheck[] = [];
  const shown = (n: number | undefined) => (n === undefined ? "(not read)" : formatUsd(n));
  const add = (name: string, a: number, b: number | undefined) => checks.push({ name, ok: b !== undefined && a === b, detail: `${shown(a)} read vs ${shown(b)} printed` });
  const items = ex.items.filter((i) => !i.outsideTotals);
  if (!items.length) return [{ name: "Line items found", ok: false, detail: "No line items were read." }];
  if (!ex.coverages.length) return [{ name: "Coverage summaries found", ok: false, detail: "No coverage summary pages were read." }];

  const bad = items.filter((it) => it.rcvCents - it.depreciationCents !== it.acvCents);
  checks.push({ name: "Each line: RCV − depreciation = ACV", ok: !bad.length, detail: bad.length ? `Lines ${bad.map((b) => b.n).join(", ")} don't add up` : `${items.length} lines` });

  const nums = ex.items.map((i) => i.n).sort((a, b) => a - b);
  const first = nums[0]!, last = nums[nums.length - 1]!;
  const missing: number[] = [];
  for (let k = first; k <= last; k++) if (!nums.includes(k)) missing.push(k);
  const dupes = nums.filter((n, i) => nums.indexOf(n) !== i);
  const lineNumCheck: ReconcileCheck = {
    name: "Line numbers complete",
    ok: !missing.length && !dupes.length,
    detail: missing.length || dupes.length ? `missing ${missing.join(", ") || "none"}; duplicated ${dupes.join(", ") || "none"}` : `lines ${first}–${last}`,
  };
  checks.push(lineNumCheck);

  const sum = (f: (i: EstimateItem) => number, list: EstimateItem[] = items) => sumCents(list, f);
  const lit = ex.lineItemTotals;
  if (lit?.rcvCents) {
    const pwiCodes = new Set(ex.coverages.filter((cv) => cv.paidWhenIncurred).map((cv) => cv.code));
    const all = sum((i) => i.rcvCents), noPwi = sum((i) => i.rcvCents, items.filter((i) => !pwiCodes.has(i.coverage)));
    const useAll = all === lit.rcvCents || noPwi !== lit.rcvCents;
    const list = useAll ? items : items.filter((i) => !pwiCodes.has(i.coverage));
    const tag = useAll ? "" : " (excluding Ordinance or Law)";
    add(`Line item RCV total${tag}`, sum((i) => i.rcvCents, list), lit.rcvCents);
    add(`Line item depreciation total${tag}`, sum((i) => i.depreciationCents, list), lit.depreciationCents);
    add(`Line item ACV total${tag}`, sum((i) => i.acvCents, list), lit.acvCents);
  }

  for (const cv of ex.coverages) {
    const its = items.filter((i) => i.coverage === cv.code);
    const mode = lineMode(ex, cv);
    const inc = mode ? [mode.taxIn && "tax", mode.opIn && "O&P"].filter(Boolean).join(" + ") : "";
    if (mode) add(`${cv.name}: line items${inc ? ` (${inc} included per line)` : ""}`, sum((i) => i.rcvCents, its), mode.targetCents);
    else add(`${cv.name}: line items`, sum((i) => i.rcvCents, its), cv.lineItemTotalCents);
    add(`${cv.name}: RCV = lines + tax + O&P`, cv.lineItemTotalCents + cv.taxCents + cv.overheadCents + cv.profitCents, cv.rcvCents);
    const rpsCut = cv.rps?.customerPortionCents ?? 0;
    add(
      `${cv.name}: depreciation split${rpsCut ? " (+ RPS customer portion)" : ""}`,
      cv.recoverableDepreciationCents + cv.nonRecoverableDepreciationCents + rpsCut,
      cv.rcvCents - cv.acvCents,
    );
    if (cv.rps) {
      const r = cv.rps;
      const rl = its.filter((i) => i.rpsTotalCents != null);
      add(`${cv.name}: RPS lines`, sum((i) => i.rcvCents, rl), r.lineItemTotalCents);
      if (r.lineItemRpsTotalCents) add(`${cv.name}: RPS line totals after factor`, sum((i) => i.rpsTotalCents ?? 0, rl), r.lineItemRpsTotalCents);
      const rpsParts = [r.lineItemTotalCents, r.taxCents, r.overheadCents, r.profitCents];
      if (rpsParts.every((x) => x !== undefined)) add(`${cv.name}: RPS RCV = lines + tax + O&P`, rpsParts.reduce((a, x) => a! + x!, 0)!, r.rcvCents);
      else checks.push({ name: `${cv.name}: RPS RCV = lines + tax + O&P`, ok: false, detail: "RPS summary not fully read" });
      if (r.rcvCents !== undefined && r.customerPortionCents !== undefined) add(`${cv.name}: RPS value = RCV - customer portion`, r.rcvCents - r.customerPortionCents, r.rpsValueCents);
      else checks.push({ name: `${cv.name}: RPS value = RCV - customer portion`, ok: false, detail: "RPS summary not fully read" });
    }
    const lineDep = sum((i) => i.depreciationCents + rpsCutCents(i), its);
    checks.push({ name: `${cv.name}: line depreciation within total`, ok: lineDep <= cv.rcvCents - cv.acvCents, detail: `${formatUsd(lineDep)} of ${formatUsd(cv.rcvCents - cv.acvCents)}` });
  }

  // Revisions skip deleted line numbers: a gap is fine when every total ties out.
  if (missing.length && !dupes.length && checks.every((k) => k.ok || k === lineNumCheck) && lit?.rcvCents) {
    lineNumCheck.ok = true;
    lineNumCheck.detail = `lines ${first}–${last}; ${missing.join(", ")} not printed (removed in a revision) and every total ties out`;
  }
  const outside = ex.items.filter((i) => i.outsideTotals);
  if (outside.length) checks.push({ name: "Code upgrade paid when incurred (not in the estimate totals)", ok: true, detail: outside.map((i) => `line ${i.n} ${formatUsd(i.rcvCents)}`).join("; ") });
  const orphan = ex.items.filter((i) => !ex.coverages.some((cv) => cv.code === i.coverage));
  if (orphan.length) checks.push({ name: "Every line has a coverage", ok: false, detail: `Lines ${orphan.map((o) => o.n).join(", ")}` });
  const deds = ex.coverages.filter((cv) => cv.deductibleCents > 0);
  checks.push({
    name: DEDUCTIBLE_CHECK_NAME,
    ok: deds.length > 0,
    detail: deds.length ? deds.map((cv) => `${cv.name} ${formatUsd(cv.deductibleCents)}`).join("; ") : "The estimate shows no deductible ($0). Confirm with the insured and enter it if there is one.",
  });
  return checks;
}

/** Checks that block using the reading (everything failing except the deductible check). Worker `failing()`. */
export function failingChecks(checks: readonly ReconcileCheck[]): ReconcileCheck[] {
  return checks.filter((k) => !k.ok && k.name !== DEDUCTIBLE_CHECK_NAME);
}

/** true when the reading ties out and the rep can continue. */
export function extractionTiesOut(checks: readonly ReconcileCheck[]): boolean {
  return failingChecks(checks).length === 0;
}

/** A normalized reading with its checks. */
export interface CheckedExtraction {
  ex: EstimateExtraction;
  checks: ReconcileCheck[];
}

/** Normalizes the AI's reading (already converted to cents) and checks it. */
export function checkExtraction(ex: EstimateExtraction): CheckedExtraction {
  const n = normalizeExtraction(ex);
  return { ex: n, checks: reconcileExtraction(n) };
}

/**
 * The Worker's two-pass decision (`extractEstimate`): if the first reading ties out, use it
 * (1 pass). Otherwise the AI was asked again (`estimateRetryFeedback`); its second reading is
 * used when it fails no more checks than the first, else the first. A second call that errored
 * is passed as undefined.
 */
export function chooseExtraction(first: CheckedExtraction, second?: CheckedExtraction): CheckedExtraction & { passes: 1 | 2 } {
  const bad = failingChecks(first.checks);
  if (!bad.length) return { ...first, passes: 1 };
  if (second && failingChecks(second.checks).length <= bad.length) return { ...second, passes: 2 };
  return { ...first, passes: 2 };
}
